import { useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Header from '@cloudscape-design/components/header';
import Button from '@cloudscape-design/components/button';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import TextFilter from '@cloudscape-design/components/text-filter';
import Table from '@cloudscape-design/components/table';
import Pagination from '@cloudscape-design/components/pagination';
import Box from '@cloudscape-design/components/box';
import Link from '@cloudscape-design/components/link';
import Modal from '@cloudscape-design/components/modal';
import { useQueryClient } from '@tanstack/react-query';
import { cohortsApi } from '../../api/cohorts';
import { useApproveMentorRequest, useRejectMentorRequest } from '../../hooks/queries/useMentors';
import { useNotifications } from '../../components/NotificationFlashbar';
import { AddMentorModal } from '../../components/AddMentorModal';
import { AssignMentorsModal } from '../../components/AssignMentorsModal';
import { queryKeys } from '../../lib/queryClient';
import '../../styles/table-hover.css';
import { badgePhotoUrl, phoneToolUrl } from '../../lib/externalUrls';

import { useIsProgramAdminOrOwner } from '../../hooks/usePermissions';

interface Mentor {
  id: string;
  name: string;
  email: string;
  mentees: string;
}

interface MentorRequest {
  id: string;
  name: string;
  email: string;
  requested: string;
  justification: string;
}

interface MentorsTabProps {
  cohortId: string;
  programId: string;
  mentors: { mentorId: string; mentorName?: string; menteeCount: number; mentees: string[] }[];
  mentorRequests: any[];
  refetchMentors: () => void;
  refetchMentorRequests: () => void;
}

export function MentorsTab({ cohortId, programId, mentors: mentorsData, mentorRequests: mentorRequestsData, refetchMentors, refetchMentorRequests }: MentorsTabProps) {
  const [mentorsFilterText, setMentorsFilterText] = useState('');
  const [requestsPageIndex, setRequestsPageIndex] = useState(1);
  const [mentorsPageIndex, setMentorsPageIndex] = useState(1);
  const [requestsSortingColumn, setRequestsSortingColumn] = useState<{ sortingField: string }>({ sortingField: 'name' });
  const [requestsSortingDescending, setRequestsSortingDescending] = useState(false);
  const [mentorsSortingColumn, setMentorsSortingColumn] = useState<{ sortingField: string }>({ sortingField: 'name' });
  const [mentorsSortingDescending, setMentorsSortingDescending] = useState(false);
  const [showAddMentorModal, setShowAddMentorModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [mentorToRemove, setMentorToRemove] = useState<Mentor | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const pageSize = 10;
  const { mutateAsync: approveMentor } = useApproveMentorRequest();
  const { mutateAsync: rejectMentor } = useRejectMentorRequest();
  const { showSuccess, showError, showInfo } = useNotifications();
  const queryClient = useQueryClient();
  const isAdmin = useIsProgramAdminOrOwner(programId);

  const mentors: Mentor[] = mentorsData.map((m, index) => ({
    id: m.mentorId || `unknown-${index}`,
    name: m.mentorName || m.mentorId || 'Unknown Mentor',
    email: `${m.mentorId}@amazon.com`,
    mentees: (m.menteeCount || 0).toString(),
  }));

  const mentorRequests: MentorRequest[] = mentorRequestsData.map((r) => ({
    id: r.requestId,
    name: r.userName || r.userId,
    email: `${r.userId}@amazon.com`,
    requested: new Date(r.requestedAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }),
    justification: r.message || 'No justification provided',
  }));

  const filteredMentors = mentors.filter(m =>
    (m.name || '').toLowerCase().includes(mentorsFilterText.toLowerCase())
  );

  // Sort mentor requests
  const sortedRequests = [...mentorRequests].sort((a, b) => {
    const field = requestsSortingColumn.sortingField as keyof MentorRequest;
    const aValue = a[field] || '';
    const bValue = b[field] || '';
    
    if (aValue < bValue) return requestsSortingDescending ? 1 : -1;
    if (aValue > bValue) return requestsSortingDescending ? -1 : 1;
    return 0;
  });

  // Sort mentors
  const sortedMentors = [...filteredMentors].sort((a, b) => {
    const field = mentorsSortingColumn.sortingField as keyof Mentor;
    const aValue = a[field] || '';
    const bValue = b[field] || '';
    
    if (aValue < bValue) return mentorsSortingDescending ? 1 : -1;
    if (aValue > bValue) return mentorsSortingDescending ? -1 : 1;
    return 0;
  });

  const requestsPagesCount = Math.ceil(sortedRequests.length / pageSize);
  const paginatedRequests = sortedRequests.slice(
    (requestsPageIndex - 1) * pageSize,
    requestsPageIndex * pageSize
  );

  const mentorsPagesCount = Math.ceil(sortedMentors.length / pageSize);
  const paginatedMentors = sortedMentors.slice(
    (mentorsPageIndex - 1) * pageSize,
    mentorsPageIndex * pageSize
  );

  const handleRequestsSortChange = (event: any) => {
    setRequestsSortingColumn(event.detail.sortingColumn);
    setRequestsSortingDescending(event.detail.isDescending || false);
  };

  const handleMentorsSortChange = (event: any) => {
    setMentorsSortingColumn(event.detail.sortingColumn);
    setMentorsSortingDescending(event.detail.isDescending || false);
  };

  const handleApproveRequest = async (requestId: string) => {
    try {
      await approveMentor({ requestId, cohortId });
      refetchMentors();
      refetchMentorRequests();
    } catch (err) {
      console.error('Failed to approve mentor request:', err);
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    try {
      await rejectMentor({ requestId, cohortId });
      refetchMentors();
      refetchMentorRequests();
    } catch (err) {
      console.error('Failed to reject mentor request:', err);
    }
  };

  const handleAutoAssign = () => setShowAssignModal(true);

  const handleRemoveMentor = async (mentorId: string) => {
    setIsRemoving(true);
    try {
      await cohortsApi.removeMentor(cohortId, mentorId);
      refetchMentors();
      showSuccess('Mentor removed', 'The mentor has been removed from the cohort.');
    } catch {
      showError('Remove failed', 'Failed to remove the mentor. Please try again.');
    } finally {
      setIsRemoving(false);
      setMentorToRemove(null);
    }
  };

  return (
    <SpaceBetween size="l">
      {isAdmin && (
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            iconName="add-plus"
            onClick={() => setShowAddMentorModal(true)}
          >
            Add mentor
          </Button>
          <Button
            onClick={handleAutoAssign}
          >
            Assign mentors to participants
          </Button>
        </SpaceBetween>
      )}
      
      {isAdmin && (
        <div className="custom-hover-table-container">
          <Table
            trackBy="id"
            wrapLines={true}
            sortingDisabled={false}
            sortingColumn={requestsSortingColumn}
            sortingDescending={requestsSortingDescending}
            onSortingChange={handleRequestsSortChange}
            header={
              <Header variant="h2" counter={`(${mentorRequests.length})`}>
                Pending mentor requests
              </Header>
            }
          pagination={
            <Pagination
              currentPageIndex={requestsPageIndex}
              pagesCount={requestsPagesCount}
              onChange={({ detail }) => setRequestsPageIndex(detail.currentPageIndex)}
            />
          }
          columnDefinitions={[
            {
              id: 'name',
              header: 'Name',
              cell: (item) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <img
                    src={badgePhotoUrl(item.id)}
                    alt={item.name}
                    style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }}
                  />
                  <span>{item.name}</span>
                </div>
              ),
              sortingField: 'name',
              isRowHeader: true,
              verticalAlign: 'top'
            },
            {
              id: 'email',
              header: 'Email',
              cell: (item) => <Link href={`mailto:${item.email}`}>{item.email}</Link>,
              sortingField: 'email',
            },
            {
              id: 'requested',
              header: 'Requested',
              cell: (item) => item.requested,
              sortingField: 'requested',
              verticalAlign: 'top'
            },
            {
              id: 'justification',
              header: 'Justification',
              cell: (item) => item.justification,
              sortingField: 'justification',
              verticalAlign: 'top'
            },
            {
              id: 'actions',
              header: 'Actions',
              cell: (item) => (
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    variant="inline-link"
                    onClick={() => handleApproveRequest(item.id)}
                  >
                    Approve
                  </Button>
                  <Button 
                    variant="inline-link"
                    onClick={() => handleRejectRequest(item.id)}
                  >
                    Reject
                  </Button>
                </SpaceBetween>
              ),
              verticalAlign: 'top'
            }
          ]}
          items={paginatedRequests}
          loadingText="Loading mentor requests"
          variant="borderless"
          empty={
            <Box textAlign="center" color="inherit" padding={{ vertical: 's' }}>
              <Box variant="p" color="inherit">No pending mentor requests.</Box>
            </Box>
          }
        />
        </div>
      )}

      <div className="custom-hover-table-container">
        <Table
          trackBy="id"
          sortingDisabled={false}
          sortingColumn={mentorsSortingColumn}
          sortingDescending={mentorsSortingDescending}
          onSortingChange={handleMentorsSortChange}
          header={
            <Header variant="h2" counter={`(${filteredMentors.length})`}>
              Mentors
            </Header>
          }
        filter={
          <TextFilter
            filteringPlaceholder="Find mentors"
            filteringText={mentorsFilterText}
            onChange={({ detail }) => setMentorsFilterText(detail.filteringText)}
            countText={`${filteredMentors.length} ${filteredMentors.length === 1 ? 'match' : 'matches'}`}
          />
        }
        pagination={
          <Pagination
            currentPageIndex={mentorsPageIndex}
            pagesCount={mentorsPagesCount}
            onChange={({ detail }) => setMentorsPageIndex(detail.currentPageIndex)}
          />
        }
        columnDefinitions={[
          {
            id: 'name',
            header: 'Name',
            cell: (item) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <img
                  src={badgePhotoUrl(item.id)}
                  alt={item.name}
                  style={{ width: 32, height: 32, borderRadius: 5, objectFit: 'cover', flexShrink: 0 }}
                />
                <div>
                  <div>{item.name}</div>
                  <div style={{ fontSize: '12px' }}>
                    <Link href={phoneToolUrl(item.id)} external fontSize="body-s">@{item.id}</Link>
                  </div>
                </div>
              </div>
            ),
            sortingField: 'name',
            isRowHeader: true,
          },
          {
            id: 'email',
            header: 'Email',
            cell: (item) => <Link href={`mailto:${item.email}`}>{item.email}</Link>,
            sortingField: 'email',
          },
          {
            id: 'mentees',
            header: 'Mentees',
            cell: (item) => item.mentees,
            sortingField: 'mentees',
          },
          ...(isAdmin ? [{
            id: 'actions',
            header: '',
            cell: (item: Mentor) => {
              const hasMentees = parseInt(item.mentees, 10) > 0;
              return (
                <ButtonDropdown
                  variant="icon"
                  items={[{
                    id: 'remove',
                    text: 'Remove',
                    disabled: hasMentees,
                    disabledReason: hasMentees
                      ? 'Reassign all mentees before removing this mentor'
                      : undefined,
                  }]}
                  ariaLabel="Mentor actions"
                  expandToViewport
                  onItemClick={() => setMentorToRemove(item)}
                />
              );
            },
          }] : [])
        ]}
        empty={
          <Box color="inherit" textAlign="center">
            <Box color="inherit" textAlign="center" variant="strong">
              No mentors
            </Box>
            <Box color="inherit" padding={{ bottom: 's' }} variant="p">
              No mentors assigned yet.
            </Box>
          </Box>
        }
        items={paginatedMentors}
        loadingText="Loading mentors"
        variant="borderless"
      />
      </div>

      <AddMentorModal
        visible={showAddMentorModal}
        cohortId={cohortId}
        onDismiss={() => setShowAddMentorModal(false)}
        onSuccess={() => refetchMentors()}
      />

      <AssignMentorsModal
        visible={showAssignModal}
        cohortId={cohortId}
        onDismiss={() => setShowAssignModal(false)}
        onSaved={(result) => {
          refetchMentors();
          queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.participants(cohortId) });
          if (result === 'no-changes') {
            showInfo('No changes', 'All assignments are already up to date.');
          } else {
            showSuccess('Assignments saved', 'Mentor assignments have been saved successfully.');
          }
        }}
      />

      {/* Remove mentor confirmation modal */}
      <Modal
        visible={!!mentorToRemove}
        onDismiss={() => setMentorToRemove(null)}
        header="Remove mentor"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setMentorToRemove(null)}>Cancel</Button>
              <Button
                variant="primary"
                loading={isRemoving}
                onClick={() => mentorToRemove && handleRemoveMentor(mentorToRemove.id)}
              >
                Remove
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Box>
          Are you sure you want to remove <strong>{mentorToRemove?.name}</strong> as a mentor?
        </Box>
      </Modal>
    </SpaceBetween>
  );
}
